import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { API } from "@/lib/apiClient";

const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const processed = useRef(false);

  const triggerDownload = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/resume/download`, { withCredentials: true });
      window.open(data.url, "_blank", "noopener");
    } catch (e) {
      // not authenticated / failed silently
    }
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const hash = window.location.hash;
    if (hash && hash.includes("session_id=") && !processed.current) {
      processed.current = true;
      const sid = hash.split("session_id=")[1].split("&")[0];
      (async () => {
        try {
          const { data } = await axios.post(
            `${API}/auth/session`,
            {},
            { headers: { "X-Session-ID": sid }, withCredentials: true }
          );
          setUser(data);
          window.history.replaceState(null, "", window.location.pathname);
          if (localStorage.getItem("pendingResume")) {
            localStorage.removeItem("pendingResume");
            setTimeout(triggerDownload, 300);
          }
        } catch {
          setUser(null);
        } finally {
          setLoading(false);
        }
      })();
    } else {
      checkAuth();
    }
  }, [checkAuth, triggerDownload]);

  const login = (returnPath = "/") => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + returnPath;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const requestResume = useCallback(() => {
    if (user) {
      triggerDownload();
    } else {
      localStorage.setItem("pendingResume", "1");
      login();
    }
  }, [user, triggerDownload]);

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {
      // ignore
    }
    setUser(null);
  };

  return (
    <AuthCtx.Provider value={{ user, loading, login, logout, requestResume }}>
      {children}
    </AuthCtx.Provider>
  );
}
